import { Calendar, Film, Drama, Trophy, Music, PartyPopper, Paintbrush, ArrowRight, ChevronRight, Newspaper, AlertTriangle, Bell, Construction, ExternalLink } from 'lucide-react';
import { Badge } from '../ui/badge';
import { ink } from '../../lib/utils';
import { avisosPortada, isUrgentAviso } from '../../lib/avisos';
import { safeHref } from '../../lib/safeHref';

const CAT_CONFIG = {
  cultura: { icon: Paintbrush, color: '#8B5CF6', label: 'Cultura' },
  cine: { icon: Film, color: '#EF4444', label: 'Cine' },
  deporte: { icon: Trophy, color: '#F59E0B', label: 'Deporte' },
  concierto: { icon: Music, color: '#3B82F6', label: 'Concierto' },
  teatro: { icon: Drama, color: '#A855F7', label: 'Teatro' },
  fiesta: { icon: PartyPopper, color: '#EC4899', label: 'Fiesta' },
  ruta: { icon: Calendar, color: '#15803d', label: 'Ruta' },
  general: { icon: Calendar, color: '#0F766E', label: 'Evento' },
};

// Get current date in Spain timezone
function getSpainDate() {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
}

function getWeekDates() {
  const today = getSpainDate();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setDate(end.getDate() + 2); // Today + tomorrow only
  return { start: today, end };
}

function formatDay(dateStr) {
  const date = new Date(dateStr + 'T00:00:00');
  const today = getSpainDate();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Hoy';
  if (date.getTime() === tomorrow.getTime()) return 'Mañana';
  return date.toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' });
}

function formatShortDate(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' });
}

export function EstaSemanaSection({ events, navigate }) {
  const { start, end } = getWeekDates();
  // Overlap semantics: an event belongs here if it is RUNNING today or tomorrow.
  // Multi-day events (exposiciones, fiestas, piscina de verano) count while
  // ongoing, not only on their start date — otherwise the section goes empty on
  // days when nothing *starts*, while the hero still says "5 eventos hoy"
  // (the hero chip already uses this same overlap definition).
  const weekEvents = (events || []).filter(e => {
    if (!e.fecha) return false;
    const evStart = new Date(e.fecha + 'T00:00:00');
    const evEnd = new Date((e.fecha_fin || e.fecha) + 'T00:00:00');
    return evStart < end && evEnd >= start;
  });

  if (weekEvents.length === 0) return null;

  // Group by day. Ongoing events (started before today) group under "Hoy" —
  // grouping them by a weeks-old start date would read as nonsense.
  const todayIso = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  const byDay = {};
  weekEvents.forEach(e => {
    const day = e.fecha >= todayIso ? e.fecha : todayIso;
    if (!byDay[day]) byDay[day] = [];
    byDay[day].push(e);
  });
  // Within a day: events that genuinely start that day first, ongoing ones after
  Object.values(byDay).forEach(list =>
    list.sort((a, b) => (a.fecha < todayIso ? 1 : 0) - (b.fecha < todayIso ? 1 : 0))
  );
  const sortedDays = Object.keys(byDay).sort();

  return (
    <div data-testid="esta-semana-section">
      <div>
        <div className="space-y-3">
          {sortedDays.map(day => (
            <div key={day}>
              <p className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5">{formatDay(day)}</p>
              <div className="space-y-1">
                {byDay[day].map((evt, i) => {
                  const cat = CAT_CONFIG[evt.categoria] || CAT_CONFIG.general;
                  const Icon = cat.icon;
                  return (
                    <button key={evt.id || i} onClick={() => navigate(`/agenda/${encodeURIComponent(evt.id)}`)}
                      className="w-full flex items-center gap-2.5 sm:gap-3 p-1.5 sm:p-2 rounded-xl hover:bg-slate-50 transition-colors text-left group">
                      {evt.imagen ? (
                        <img src={evt.imagen} alt="" className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover shrink-0" loading="lazy" />
                      ) : (
                        <div className="w-10 h-10 sm:w-12 sm:h-12 shrink-0 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${cat.color}15` }}>
                          <Icon size={16} style={{ color: ink(cat.color) }} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="text-[11px] sm:text-xs font-bold text-torre-slate truncate group-hover:text-torre-teal transition-colors">{evt.titulo}</p>
                        <div className="flex items-center gap-1 sm:gap-1.5 mt-0.5">
                          <Badge className="text-[11px] sm:text-[11px] rounded-full px-1.5 py-0 shrink-0" style={{ backgroundColor: `${cat.color}15`, color: ink(cat.color), borderColor: `${cat.color}30` }}>{cat.label}</Badge>
                          {evt.estado && <span className="text-[11px] font-bold text-red-800 shrink-0">{evt.estado.charAt(0).toUpperCase() + evt.estado.slice(1)}</span>}
                          {evt.hora && evt.hora !== '00:00' && <span className="text-[11px] sm:text-[11px] text-slate-400 shrink-0">{evt.hora}h</span>}
                          {evt.lugar && <span className="text-[11px] sm:text-[11px] text-slate-400 truncate">· {evt.lugar.split(',')[0]}</span>}
                          {evt.fecha < todayIso && evt.fecha_fin && evt.fecha_fin > todayIso && (
                            <span className="text-[11px] sm:text-[11px] text-slate-400 shrink-0">· hasta el {formatShortDate(evt.fecha_fin)}</span>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export function AvisosSection({ bulletins, obras, navigate }) {
  // Council avisos of the last 48 hours, and the closures of the obras list that are on today or
  // start tomorrow, each shown until it is over (lib/avisos.js). A closure approved from a council
  // post is listed once, as the closure.
  const { obras: cortes, boletines: avisos } = avisosPortada(bulletins, obras, Date.now());

  if (avisos.length === 0 && cortes.length === 0) return null;

  // Separate urgent (cuts, live emergencies and alerts) from regular avisos (lib/avisos.js)
  const urgent = avisos.filter(isUrgentAviso);
  const regular = avisos.filter(b => !urgent.includes(b));
  // Water/power cuts and emergencies are urgent too, but only a traffic cut gets the traffic heading
  const allTraffic = urgent.every(b => b.aviso_type === 'corte_trafico');

  const soloCortes = cortes.every((o) => o.zone.tipo === 'corte_total' || o.zone.tipo === 'corte_parcial');

  return (
    <div className="mb-4 animate-fade-in-up" data-testid="avisos-section">
      {/* Closures and urgent works from the obras list (the map's), while they last */}
      {cortes.length > 0 && (
        <div className="card-solid p-3 border-l-4 border-l-red-500 mb-2" data-testid="avisos-obras">
          <div className="flex items-center gap-1.5 mb-2">
            <Construction size={13} className="text-red-500" aria-hidden="true" />
            <span className="text-xs font-extrabold text-red-700">{soloCortes ? 'Cortes de tráfico' : 'Obras y cortes'}</span>
          </div>
          <ul className="space-y-1">
            {cortes.map((o) => {
              const href = safeHref(o.fuente_url);
              return (
                <li key={o.id}>
                  <button type="button" onClick={() => navigate('/mapa?layer=construction')}
                    className="w-full text-left p-1.5 rounded-lg hover:bg-red-50/50 transition-colors">
                    <p className="text-xs text-red-800 leading-snug font-medium line-clamp-2">{o.texto}</p>
                    <span className="text-[11px] text-red-700 mt-0.5 block">
                      {o.dgt ? `DGT${o.detalle ? ` · ${o.detalle}` : ''} · ` : ''}Ver en el mapa
                    </span>
                  </button>
                  {href && (
                    <a href={href} target="_blank" rel="noopener noreferrer"
                      className="ml-1.5 text-[11px] font-bold text-torre-teal underline inline-flex items-center gap-0.5">
                      Aviso del {o.zone.fuente || 'Ayuntamiento'} <ExternalLink size={10} aria-hidden="true" />
                      <span className="sr-only"> (se abre en otra pestaña)</span>
                    </a>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Urgent cortes — red banner */}
      {urgent.length > 0 && (
        <div className="card-solid p-3 border-l-4 border-l-red-500 mb-2">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle size={13} className="text-red-500" />
            <span className="text-xs font-extrabold text-red-700">{allTraffic ? 'Corte de tráfico' : 'Aviso urgente'}</span>
          </div>
          {urgent.map((b, i) => (
            <button key={b.id || i} onClick={() => navigate(`/boletines?filtro=avisos&destacado=${encodeURIComponent(b.id || '')}`)}
              className="w-full text-left p-1.5 rounded-lg hover:bg-red-50/50 transition-colors">
              <p className="text-xs text-red-800 leading-snug font-medium line-clamp-2">{b.simplificado || b.titulo}</p>
              {b.fecha && (
                <span className="text-[11px] text-red-400 mt-0.5 block">{new Date(b.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })}</span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Regular avisos — amber banner */}
      {regular.length > 0 && (
        <div className="card-solid p-3 border-l-4 border-l-amber-400">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <Bell size={13} className="text-amber-500" />
              <span className="text-xs font-extrabold text-torre-slate">Avisos del Ayuntamiento</span>
            </div>
            <button onClick={() => navigate('/boletines?filtro=avisos')} className="text-[11px] text-torre-teal font-bold hover:underline flex items-center gap-0.5">
              Ver todos <ArrowRight size={9} />
            </button>
          </div>
          <div className="space-y-1.5">
            {regular.map((b, i) => (
              <button key={b.id || i} onClick={() => navigate(`/boletines?filtro=avisos&destacado=${encodeURIComponent(b.id || '')}`)}
                className="w-full text-left p-1.5 rounded-lg hover:bg-amber-50/50 transition-colors group">
                <p className="text-xs text-slate-600 leading-snug line-clamp-2">{b.simplificado || b.titulo}</p>
                <div className="flex items-center gap-2 mt-1">
                  {b.fecha && (
                    <span className="text-[11px] text-slate-400">{new Date(b.fecha).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', timeZone: 'Europe/Madrid' })}</span>
                  )}
                  {b.categorias && b.categorias.filter(c => c.toLowerCase() !== 'avisos')[0] && (
                    <Badge className="text-[11px] bg-amber-50 text-amber-600 border-amber-200/50 px-1.5 py-0 rounded-full">{b.categorias.filter(c => c.toLowerCase() !== 'avisos')[0]}</Badge>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
